import React, { useState, useEffect } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Button } from 'Components/frameworks/button/Button';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { Text } from 'Components/frameworks/text/Text';
import { Tooltip } from 'Components/frameworks/tooltip/Tooltip';
declare type ctaActionsType = 'add' | 'remove' | 'disabled' | 'included' | 'none';
export const ctaActionTypes = {
  add: 'add',
  remove: 'remove',
  disabled: 'disabled',
  included: 'included',
  none: 'none',
};
type AddRemoveButtonProps = {
  price?: string;
  prestation?: string;
  dataSelector?: string;
  ctaType?: ctaActionsType;
  onClick?: (buttonAction: string) => void;
  getTrackingData?: (arg0: string) => TrackingData;
  isColumn?: boolean;
};
type Props = {
  addLabel?: string;
  className?: string;
  ctaButton?: Node;
  headColor?: Colors;
  icon: IconName;
  imageUrl?: string;
  includedLabel?: string;
  label: string;
  reassurance?: string;
  removeLabel?: string;
  text?: string;
  tooltip?:
    | {
        children: Node;
        dataSelector?: string;
      }
    | null
    | undefined;
  trackOpenTooltip?: () => void;
} & AddRemoveButtonProps;

const AddRemoveButton = ({
  isAdd,
  price,
  ctaLabel,
  prestation = '',
  dataSelector = '',
  ctaType = '',
  isLoading,
  getTrackingData,
  handleClick,
  isColumn,
}: {
  isAdd?: boolean;
  ctaLabel: string;
  isLoading?: boolean;
  handleClick: (buttonAction: string) => void;
} & AddRemoveButtonProps) => {
  const track = useTracking();
  const buttonAction = isAdd ? ctaActionTypes.add : ctaActionTypes.remove;
  const label = price ? `${ctaLabel} ${prestation} ${price}` : ctaLabel;
  return (
    <Button
      className={classnames('w-full', {
        'sm:w-auto': !isColumn,
      })}
      color={isAdd ? COLORS.BLACK : COLORS.BLACK}
      colorBg={isAdd ? COLORS.SAFFRON : COLORS.TRANSPARENT}
      colorBorder={isAdd ? COLORS.SAFFRON : COLORS.BLACK}
      data-selector={`${dataSelector}-${ctaType}`}
      isDisabled={ctaType === 'disabled'}
      isLoading={isLoading}
      onClick={() => {
        handleClick(buttonAction);

        if (getTrackingData) {
          track(getTrackingData(buttonAction));
        }
      }}
    >
      {label}
    </Button>
  );
};

const ButtonRenderer = ({
  ctaType,
  ctaButton,
  includedLabel,
  addLabel,
  removeLabel,
  price,
  ...props
}) => {
  if (ctaButton) {
    return ctaButton;
  }

  switch (ctaType) {
    case ctaActionTypes.add:
    case ctaActionTypes.disabled:
      return (
        <AddRemoveButton
          isAdd
          ctaType={ctaType}
          ctaLabel={addLabel}
          price={price ? `+${price}` : ''}
          {...props}
        />
      );

    case ctaActionTypes.remove:
      return (
        <AddRemoveButton
          ctaType={ctaType}
          ctaLabel={removeLabel}
          price={price ? `-${price}` : ''}
          {...props}
        />
      );

    case ctaActionTypes.included:
      return <span className="font-brand text-18 text-saffron">{includedLabel}</span>;

    default:
      return null;
  }
};

export const PaidService = ({
  className,
  ctaButton = '',
  headColor = COLORS.SIENNA,
  icon = null,
  imageUrl = '',
  isColumn = false,
  label = '',
  reassurance = '',
  text = '',
  tooltip = '',
  trackOpenTooltip,
  getTrackingData = '',
  dataSelector = 'PaidService',
  prestation = '',
  removeLabel = '',
  addLabel = '',
  price = '',
  includedLabel = '',
  ctaType = ctaActionTypes.none,
  onClick,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [ctaType]);

  const handleClick = (buttonAction): void => {
    setIsLoading(true);
    onClick && onClick(buttonAction);
  };

  return (
    <Capsule className={className} header="none">
      <div className="isolate flex-wrap sm:flex" data-testid="PaidService">
        {!!imageUrl && !isColumn && (
          <div className="sm:w-2/5">
            <Image
              className="relative h-full"
              classNameImg="md:absolute"
              imageUrl={imageUrl}
              dimensions={{
                w: 600,
                h: 428,
              }}
              transformation="resize"
            />
          </div>
        )}
        <div
          className={classnames('p-20', {
            'sm:w-3/5': !!imageUrl && !isColumn,
          })}
        >
          <div className="mb-20 flex items-center gap-10 font-brand text-18 font-bold">
            <Icon className={`shrink-0 text-${headColor}`} name={icon} width="2.125rem" />
            <div className={`leading-tight text-${headColor}`} data-testid="PaidService-label">
              {label}
            </div>
            {!!tooltip && (
              <Tooltip
                {...tooltip}
                renderTrigger={(toggle) => (
                  <button
                    className="ms-10 leading-0 text-saffron"
                    data-selector={tooltip.dataSelector}
                    onClick={() => {
                      trackOpenTooltip && trackOpenTooltip();
                      toggle();
                    }}
                    type="button"
                  >
                    <Icon name={ICONS.INFORMATION} width="1.25rem" />
                  </button>
                )}
              />
            )}
          </div>
          <Text text={text} />
          <Text className="mt-10" text={reassurance} theme="text-sans-n2" />
        </div>
        <div className="border-t-1 p-20 sm:w-full">
          <ButtonRenderer
            ctaButton={ctaButton}
            ctaType={ctaType}
            includedLabel={includedLabel}
            price={price}
            addLabel={addLabel}
            removeLabel={removeLabel}
            prestation={prestation}
            dataSelector={dataSelector}
            isLoading={isLoading}
            getTrackingData={getTrackingData}
            handleClick={handleClick}
            isColumn={isColumn}
          />
        </div>
      </div>
    </Capsule>
  );
};
